import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Layouts
import { AdminLayout, ContractorLayout, WorkerLayout, ClientLayout, RelatorLayout } from "./components/Layout";

import { ImpersonationBanner } from "./components/common/ImpersonationBanner";

// Auth Pages
import LoginPage from "./pages/LoginPage";
import ValidateCertificatePage from "./pages/ValidateCertificatePage";
import PublicTestPage from "./pages/PublicTestPage";
import CompetenciasPage from "./pages/CompetenciasPage";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import UsersPage from "./pages/admin/UsersPage";
import CompaniesPage from "./pages/admin/CompaniesPage";
import CoursesPage from "./pages/admin/CoursesPage";
import TrainingsPage from "./pages/admin/TrainingsPage";
import QuestionsPage from "./pages/admin/QuestionsPage";
import CertificatesPage from "./pages/admin/CertificatesPage";
import CompanyDetailPage from "./pages/admin/CompanyDetailPage";
import CourseConfigPage from "./pages/admin/CourseConfigPage";
import TrainingDetailPage from "./pages/admin/TrainingDetailPage";
import TestBankPage from "./pages/admin/TestBankPage";
import SettingsPage from "./pages/admin/SettingsPage";
import DocumentsPage from "./pages/admin/DocumentsPage";
import MandantesPage from "./pages/admin/MandantesPage";
import MandanteDetailPage from "./pages/admin/MandanteDetailPage";
import MassNotificationPage from "./pages/admin/MassNotificationPage";
import ReportsPage from "./pages/admin/ReportsPage";
import MessagesPage from "./pages/MessagesPage";

// Worker Pages
import WorkerDashboard from "./pages/worker/WorkerDashboard";
import WorkerTrainingsPage from "./pages/worker/WorkerTrainingsPage";
import WorkerEvaluationsPage from "./pages/worker/WorkerEvaluationsPage";
import WorkerCertificatesPage from "./pages/worker/WorkerCertificatesPage";

// Contractor Pages
import ContractorDashboard from "./pages/contractor/ContractorDashboard";
import ContractorTrainingsPage from "./pages/contractor/ContractorTrainingsPage";
import ContractorWorkersPage from "./pages/contractor/ContractorWorkersPage";
import ContractorResultsPage from "./pages/contractor/ContractorResultsPage";
import ContractorConsolidatedPage from "./pages/contractor/ContractorConsolidatedPage";

// Client Pages
import ClientDashboard from "./pages/client/ClientDashboard";

// Relator Pages
import RelatorDashboard from "./pages/relator/RelatorDashboard";
import RelatorTrainingDetail from "./pages/relator/RelatorTrainingDetail";

// Shared Pages
import ProfilePage from "./pages/ProfilePage";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading, isImpersonating } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // When impersonating, allow access to the impersonated user's routes
  if (allowedRoles && !allowedRoles.includes(user?.role) && !isImpersonating) {
    switch (user?.role) {
      case 'admin':
        return <Navigate to="/admin" replace />;
      case 'contractor':
        return <Navigate to="/contractor" replace />;
      case 'worker':
        return <Navigate to="/worker" replace />;
      case 'client':
        return <Navigate to="/client" replace />;
      case 'relator':
        return <Navigate to="/relator" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  }

  return children;
};

// Public Route (redirects authenticated users)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" />
      </div>
    );
  }

  if (isAuthenticated) {
    switch (user?.role) {
      case 'admin':
        return <Navigate to="/admin" replace />;
      case 'contractor':
        return <Navigate to="/contractor" replace />;
      case 'worker':
        return <Navigate to="/worker" replace />;
      case 'client':
        return <Navigate to="/client" replace />;
      case 'relator':
        return <Navigate to="/relator" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  }

  return children;
};

// Placeholder components for incomplete pages
const PlaceholderPage = ({ title }) => (
  <div className="text-center py-12">
    <h1 className="text-2xl font-bold text-white mb-4">{title}</h1>
    <p className="text-gray-400">Esta página está en desarrollo</p>
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/validate" element={<ValidateCertificatePage />} />
      <Route path="/validar" element={<ValidateCertificatePage />} />
      <Route path="/validar/:code" element={<ValidateCertificatePage />} />
      <Route path="/prueba/:trainingId" element={<PublicTestPage />} />
      <Route path="/competencias/:workerId" element={<CompetenciasPage />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="companies" element={<CompaniesPage />} />
        <Route path="companies/:companyId" element={<CompanyDetailPage />} />
        <Route path="mandantes" element={<MandantesPage />} />
        <Route path="mandantes/:mandanteId" element={<MandanteDetailPage />} />
        <Route path="courses" element={<CoursesPage />} />
        <Route path="courses/:courseId/config" element={<CourseConfigPage />} />
        <Route path="trainings" element={<TrainingsPage />} />
        <Route path="trainings/:trainingId" element={<TrainingDetailPage />} />
        <Route path="questions" element={<QuestionsPage />} />
        <Route path="test-bank" element={<TestBankPage />} />
        <Route path="certificates" element={<CertificatesPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="notifications" element={<MassNotificationPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* Contractor Routes */}
      <Route path="/contractor" element={
        <ProtectedRoute allowedRoles={['contractor', 'admin']}>
          <ContractorLayout />
        </ProtectedRoute>
      }>
        <Route index element={<ContractorDashboard />} />
        <Route path="workers" element={<ContractorWorkersPage />} />
        <Route path="trainings" element={<ContractorTrainingsPage />} />
        <Route path="results" element={<ContractorResultsPage />} />
        <Route path="enrollments" element={<ContractorResultsPage />} />
        <Route path="certificates" element={<ContractorConsolidatedPage />} />
        <Route path="consolidated" element={<ContractorConsolidatedPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* Worker Routes */}
      <Route path="/worker" element={
        <ProtectedRoute allowedRoles={['worker', 'admin']}>
          <WorkerLayout />
        </ProtectedRoute>
      }>
        <Route index element={<WorkerDashboard />} />
        <Route path="trainings" element={<WorkerTrainingsPage />} />
        <Route path="evaluations" element={<WorkerEvaluationsPage />} />
        <Route path="certificates" element={<WorkerCertificatesPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* Client (Mandante) Routes */}
      <Route path="/client" element={
        <ProtectedRoute allowedRoles={['client', 'admin']}>
          <ClientLayout />
        </ProtectedRoute>
      }>
        <Route index element={<ClientDashboard />} />
        <Route path="trainings" element={<PlaceholderPage title="Capacitaciones" />} />
        <Route path="reports" element={<PlaceholderPage title="Reportes" />} />
      </Route>

      {/* Relator Routes */}
      <Route path="/relator" element={
        <ProtectedRoute allowedRoles={['relator', 'admin']}>
          <RelatorLayout />
        </ProtectedRoute>
      }>
        <Route index element={<RelatorDashboard />} />
        <Route path="trainings/:trainingId" element={<RelatorTrainingDetail />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ImpersonationBanner />
        <AppRoutes />
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#111111',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#fff',
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
